<template>
  <v-app-bar
    :key="activeDashboardIndex"
    app
    :style="{backgroundColor: `${navBarColor} !important`}"
    extension-height="55"
    elevate-on-scroll
  >
    <DashLogo
      v-if="logo"
      :logo="logo"
      class="pl-2"
    />
    <v-spacer />

    <TrialStudio
      v-if="isEditor"
      :account-id="accountId"
      :universe-id="universeId"
      :user="user"
      is-studio
      class="trial-studio"
    />

    <div v-if="isStaging">
      <StIcon
        v-if="isMobile"
        v-tooltip="isStagingTooltip"
        icon-name="alert"
        color="warning"
        class="mr-3"
      />
      <v-chip
        v-else
        v-tooltip="isStagingTooltip"
        pill
        color="warning"
        text-color="black"
        class="mr-5"
      >
        <StIcon icon-name="alert" />
        {{ isStagingTitleChip }}
      </v-chip>
    </div>

    <div
      v-if="showDashboardSelector"
      class="dashboard-select-container"
    >
      <v-autocomplete
        :value="currentDashboardKey"
        :items="accessibleDashboardsFormatted"
        :filter="filterDashboardOptions"
        :placeholder="dashboardSelectPlaceholder"
        class="dashboard-select-container__select"
        solo
        @change="navigateToSelectedDashboard"
      />
    </div>

    <HelpButton />

    <LanguageSelector />

    <UserMenu />

    <template v-slot:extension>
      <GlobalFilter />
    </template>
  </v-app-bar>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import UserMenu from '@/components/UserMenu';
import DashLogo from '@/components/dashboard/DashLogo';
import StIcon from '@stilingue/st-icon';
import TrialStudio from '@stilingue/st-trial-studio';
import GlobalFilter from '@/components/filter/GlobalFilter';
import HelpButton from '@/components/HelpButton';
import LanguageSelector from '../LanguageSelector.vue';
import AccountsAndPermissionsService from '@/services/AccountsAndPermissionsService';
import { getDashboardsForUserInUniverse } from '@/utils/dashboardAccess';
import { navigateToDashboard } from '@/utils/dashboardNavigation';
import { DASHBOARD_MODELS } from '@/utils/constants';

function addUniqueAccount(account, accountsById) {
  const accountId = String(account.id);
  if (!accountsById.has(accountId)) {
    accountsById.set(accountId, { id: accountId, name: account.name, universeIds: new Set() });
  }
  const uniqueAccount = accountsById.get(accountId);
  const universeIds = Array.isArray(account.universeIds) ? account.universeIds : [];
  universeIds.forEach(universeId => uniqueAccount.universeIds.add(String(universeId)));
}

function getUniqueAccountsById(accountsAndPermissions) {
  const accountsById = new Map();
  for (const item of accountsAndPermissions) {
    const account = item && item.account;
    if (!account || account.id === undefined || account.id === null) {
      continue;
    }
    addUniqueAccount(account, accountsById);
  }
  return accountsById;
}

function getAccountsByUniverse(accountsById) {
  const accountsByUniverse = new Map();
  for (const account of accountsById.values()) {
    for (const universeId of account.universeIds) {
      if (!accountsByUniverse.has(universeId)) {
        accountsByUniverse.set(universeId, []);
      }
      accountsByUniverse.get(universeId).push(account);
    }
  }
  return accountsByUniverse;
}

function getUniqueDashboardOptions(dashboardsByUniverse) {
  const dashboardsById = new Map();
  dashboardsByUniverse.flat().forEach((dashboard) => {
    if (!dashboardsById.has(dashboard.key)) {
      dashboardsById.set(dashboard.key, dashboard);
    }
  });
  return [...dashboardsById.values()];
}
export default {
  name: 'DashTopBar',
  components: {
    UserMenu,
    DashLogo,
    StIcon,
    TrialStudio,
    GlobalFilter,
    HelpButton,
    LanguageSelector,
  },
  data() {
    return {
      accessibleDashboards: [],
    };
  },
  computed: {
    dashboardSelectlabel() {
      return this.$t('components.DashTopBar.label.dashboardSelectlabel');
    },
    dashboardSelectPlaceholder() {
      return this.$t('components.DashTopBar.label.dashboardSelectPlaceholder');
    },
    noTitleDasboard() {
      return this.$t('components.DashTopBar.label.noTitleDashboard');
    },
    isStagingTooltip() {
      return this.$t('components.DashTopBar.label.isStagingTooltip');
    },
    isStagingTitleChip() {
      return 'Staging';
    },
    ...mapState('dashboard', [
      'activeDashboardIndex',
      'isStaging',
    ]),
    ...mapState('user', [
      'user',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
      'activeLanguage',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    navBarColor() {
      if (this.activeDashboard && this.activeDashboard.data && this.activeDashboard.data.color) {
        return this.activeDashboard.data.color;
      }
      return null;
    },
    logo() {
      if (this.activeDashboard && this.activeDashboard.data && this.activeDashboard.data.logo) {
        return this.activeDashboard.data.logo;
      }
      return null;
    },
    accessibleDashboardsFormatted() {
      return this.accessibleDashboards.map((dashboard) => {
        const title = dashboard.data.title || this.noTitleDasboard;
        const text = `${dashboard.accountName} / ${title}`;
        return {
          text,
          value: dashboard.key,
          searchableText: `${dashboard.accountName} ${title}`,
        };
      });
    },
    currentDashboardKey() {
      const { accountId, universeId } = this.$route.params;
      const dashboardId = this.$route.query.dashboard ||
        (this.activeDashboard && this.activeDashboard.id) ||
        DASHBOARD_MODELS[0].id;
      return `${accountId}/${universeId}/${dashboardId}`;
    },
    showDashboardSelector() {
      return this.user && this.accessibleDashboardsFormatted.length > 1;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (user && user.email) {
          this.loadAccessibleDashboards();
        }
      },
    },
  },
  methods: {
    filterDashboardOptions(item, queryText) {
      const searchableText = item.searchableText || item.text || '';
      return searchableText.toLowerCase().includes(String(queryText || '').toLowerCase());
    },
    async loadAccessibleDashboards() {
      try {
        const service = new AccountsAndPermissionsService(this.isStaging);
        const accountsAndRoles = await service.getAccountsAndPermissions(this.user.email);
        const accountsByUniverse = getAccountsByUniverse(getUniqueAccountsById(accountsAndRoles));
        const dashboardsByUniverse = await Promise.all([...accountsByUniverse].map(async ([universeId, accounts]) => {
          const dashboards = await getDashboardsForUserInUniverse(universeId, this.user.email);
          return accounts.flatMap(account => dashboards.map(dashboard => {
            const dashboardId = String(dashboard.id);
            const key = `${account.id}/${universeId}/${dashboardId}`;
            return {
              ...dashboard,
              accountId: account.id,
              accountName: account.name,
              universeId,
              dashboardId,
              key,
            };
          }));
        }));

        this.accessibleDashboards = getUniqueDashboardOptions(dashboardsByUniverse);
      } catch (error) {
        console.warn('[DashTopBar] Failed to load accessible dashboards; selector will be unavailable.');
        this.accessibleDashboards = [];
      }
    },
    navigateToSelectedDashboard(key) {
      const dashboard = this.accessibleDashboards.find(item => item.key === key);
      if (dashboard) {
        navigateToDashboard(this.$router, dashboard);
      }
    },
  },
};
</script>

<style scoped lang="scss">
  .v-app-bar {
    z-index: 8 !important;
    max-height: 112px;
  }

  .dashboard-select-container {
    display: flex;
    justify-content: flex-end;
    min-width: 160px;

    &__select {
      position: relative;
      min-width: auto !important;
      height: 24px !important;
      padding: 0  var(--ds-spacing-stack-quark) 0 var(--ds-spacing-stack-sm) !important;
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-dense);
      text-align: left;
      text-transform: none;
    }
  }

  .trial-studio {
    z-index: 1;
  }

  @media only screen and (max-width: 640px) {
    .dashboard-select-container {
      width: 165px;
      padding-right: 16px;
    }
  }

  ::v-deep {
    .v-select__selection.v-select__selection--comma {
      line-height: 1.4;
    }

    .v-toolbar__content {
      border-bottom: 1px solid var(--ds-color-gray-3);
      height: 56px !important;
      z-index: 2;
    }

    .v-toolbar__extension {
      padding: 0;
      position: static;
    }

    .v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
      box-shadow: 0;
      height: 20px;
    }

    .v-text-field__details {
      display: none;
    }

    .v-text-field--enclosed:not(.v-input__prepend-outer) > .v-input__control > .v-input__slot {
      background-color: transparent !important;
      padding: 0 0 0 4px;
      box-shadow: none;

      &:hover {
        background-color: var(--ds-color-background) !important;
      }
    }

    .v-text-field.v-text-field--solo .v-input__control {
      min-height: 25.5px;
    }

    .v-input__icon--append > .v-icon {
      color: var(--ds-color-icon-2);
    }

    .v-select {
      &__selections input {
        display: none;
      }

      &__selection {
        max-width: none;
      }
    }
  }
</style>
